import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Ferrari Testarossa (1984) — the flat, VERY WIDE mid-engine wedge at true
// scale (1 unit = 1 metre): length ~4.49, width 1.98 (famously WIDE), a very
// LOW planted stance, wheelbase 2.55 (front axle +1.30, rear -1.25), ~0.32 m
// wheels. Nose is +Z. Authored for the `cars` showcase batch; height/stance
// referenced against the accepted ferrari-f40.ts (same class of low wide wedge).
//
// The silhouette is ONE continuous LOW line, never a three-box and never a
// pickup: a low pointed nose with flush pop-up headlamps -> a STEEPLY raked
// windscreen -> a low flat cabin cap -> a dark SLATTED engine cover (never an
// open deck) -> a low kicked-up tail. The signature cues are the LONG
// horizontal "cheese-grater" SIDE STRAKES running the whole flank from behind
// the front wheel and flaring into the wide rear intakes, the DRAMATIC wide
// rear haunches (the tail is far wider than the nose), and the full-width
// horizontal SLATTED TAIL band with the lamps hidden behind it over a
// body-colour bumper. Body panels carry the "paint" recolour slot (Rosso
// Corsa); `drive` spins the wheels and `hazards` blinks four indicators.

const PAINT = "#c00d1e"; // Rosso Corsa — the "paint" slot default
const STRAKE = "#141518"; // black side strakes + rear intakes (the cheese grater)
const TRIM = "#141518"; // gloss black (louvres, pillars, cover base)
const CLAD = "#202225"; // lower valances, sills, splitter
const WELL = "#141518"; // wheel-well discs
const GLASS = "#1b2129"; // smoked side / rear glazing
const SCREEN = "#25313c"; // windscreen (a shade lighter than the tint)
const CABIN = "#0e0f12"; // interior block behind the glass
const MESH = "#0d0e10"; // tail slats + intake mesh (near-black)
const SPOKE = "#d7dce1"; // bright five-spoke alloys
const DISH = "#101114"; // dark wheel dish behind the spokes
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#9fb0ba"; // pop-up headlamp lens — toned down, not a white sticker
const AMBER = "#e8a13a";
const TAIL_RED = "#c41726"; // tail lamp band behind the slats
const CHROME = "#ccd1d6";
const EXHAUST = "#2b2e33"; // exhaust tip metal (dark gunmetal)
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). The car is LOW and WIDE — the belt line sits at
// ~0.60 and the roof peaks at only ~1.0 (F40-low), while the doors run 1.80
// wide and the rear haunches flare to a full 2.0, so the profile is one flat
// wedge and the tail reads clearly wider than the nose.
const WHEEL_R = 0.32;
const AXLE_F = 1.3;
const AXLE_R = -1.25; // wheelbase 2.55
const WHEEL_X_F = 0.86; // half track, front — wheels at the corners
const WHEEL_X_R = 0.88; // half track, rear (fat tyres under the wide hips)

const SCREEN_RAKE = 0.95; // STEEPLY reclined windscreen (top far toward the tail)
const HOOD_SLOPE = 0.1; // frunk lid diving to the low pointed nose
const COVER_SLOPE = 0.068; // slatted engine cover falling gently toward the tail

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One five-spoke alloy over a low-profile tyre: tyre, brake disc, dark dish,
 *  five bright spokes proud of the tyre face, chrome centre cap. Every part
 *  shares `name` so the `drive` track spins them as one node about the axle.
 *  `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, halfTrack: number, tyreW: number, z: number, name: string): Part[] {
  const y = WHEEL_R;
  const outer = halfTrack + tyreW / 2; // tyre outer face — stays proud of the well disc
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, tyreW, 18, TIRE, [side * halfTrack, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.13, 0.13, 0.03, 14, BRAKE, [side * (halfTrack - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.2, 0.2, 0.06, 18, DISH, [side * (outer - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.055, 0.36, 0.085, SPOKE, [side * (outer - 0.02), y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.05, 0.05, 0.028, 12, CHROME, [side * (outer - 0.002), y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a body-coloured half-torus brow over the tyre. The [0, pi/2, 0] yaw
 *  puts the 0..pi arc exactly over the top half. */
function arch(side: 1 | -1, z: number, discX: number, discR: number, browR: number, browTube: number): Part[] {
  return [
    cyl(discR, discR, 0.04, 18, WELL, [side * discX, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(browR, browTube, 6, 18, Math.PI),
      color: PAINT,
      position: [side * (discX + 0.015), WHEEL_R, z],
      rotation: [0, Math.PI / 2, 0],
      slot: "paint",
    },
  ];
}

// The five band-heights the horizontal side strakes ride at — the SAME list is
// used for the door segment and the rear-intake segment so the blades read as
// one continuous horizontal grater flaring into the intake.
const STRAKE_Y = [0.37, 0.43, 0.49, 0.55, 0.61];

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair flanks the nose intake; rear pair sits at the ends of the tail band.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.56, 0.44, 2.2], size: [0.15, 0.04, 0.04] },
  { name: "indicator-fr", pos: [0.56, 0.44, 2.2], size: [0.15, 0.04, 0.04] },
  { name: "indicator-rl", pos: [-0.82, 0.6, -2.15], size: [0.14, 0.05, 0.03] },
  { name: "indicator-rr", pos: [0.82, 0.6, -2.15], size: [0.14, 0.05, 0.03] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear (wide track — wheels planted at the corners) --------------
  P.push(
    ...wheel(-1, WHEEL_X_F, 0.26, AXLE_F, "wheel-fl"), ...wheel(1, WHEEL_X_F, 0.26, AXLE_F, "wheel-fr"),
    ...wheel(-1, WHEEL_X_R, 0.3, AXLE_R, "wheel-rl"), ...wheel(1, WHEEL_X_R, 0.3, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
  }
  for (const s of S) {
    P.push(...arch(s, AXLE_F, 0.905, 0.42, 0.39, 0.05)); // front arch
    P.push(...arch(s, AXLE_R, 1.005, 0.47, 0.44, 0.055)); // flared rear arch over the wide hips
  }
  P.push(box(1.7, 0.12, 3.5, TRIM, [0, 0.2, -0.05])); // underbody pan

  // -- Body masses (paint) — a low wide tub (belt at only 0.60) ---------------
  P.push(paint(1.8, 0.34, 3.6, [0, 0.44, -0.05])); // main tub, sill to belt line

  // Low pointed wedge nose: a very low front face, a hood diving to it, and
  // fender crowns beside the hood — the whole top reads as one falling line.
  P.push(paint(1.56, 0.15, 0.14, [0, 0.42, 2.16])); // low front prow face
  P.push(paint(1.44, 0.06, 1.5, [0, 0.56, 1.35], [HOOD_SLOPE, 0, 0])); // frunk lid diving to the nose
  for (const s of S) P.push(paint(0.24, 0.07, 1.5, [s * 0.81, 0.585, 1.35], [HOOD_SLOPE, 0, 0])); // fender crowns
  P.push(box(1.62, 0.14, 0.16, CLAD, [0, 0.26, 2.08])); // front splitter under the prow
  P.push(box(1.5, 0.12, 0.3, CLAD, [0, 0.24, 1.9])); // valance filling back toward the arch
  P.push(box(1.1, 0.07, 0.05, MESH, [0, 0.36, 2.2])); // thin full-width front intake slot

  // -- Rear haunches: DRAMATIC wide hips, flaring to the full 2.0 -------------
  // The hip rises the FULL flank height (up to the deck), so from the side the
  // engine bay is hidden behind body-colour metal — no black cavity.
  for (const s of S) {
    P.push(paint(0.22, 0.64, 1.62, [s * 0.89, 0.55, -1.03])); // muscular hip, outer edge at 1.00, up to the deck
  }

  // -- Engine bay + slatted cover (fills the space — never an open deck) ------
  P.push(box(1.42, 0.36, 1.2, MESH, [0, 0.62, -1.08])); // solid engine bay block under the lid
  P.push(paint(1.44, 0.05, 1.12, [0, 0.84, -1.08], [-COVER_SLOPE, 0, 0])); // body-colour engine lid (reads as a surface, not a void)
  for (let i = 0; i < 7; i++) {
    const z = -0.65 - 0.14 * i; // black transverse louvres proud of the red lid — red shows between
    const y = 0.888 + COVER_SLOPE * (z + 0.62);
    P.push(box(1.38, 0.028, 0.06, MESH, [0, y, z], [-COVER_SLOPE, 0, 0]));
  }
  P.push(paint(1.58, 0.12, 0.5, [0, 0.8, -1.72])); // solid deck lip closing the tail top

  // -- Greenhouse (LOW flat cap; steep screen; sealed, flush to the deck) -----
  P.push(box(1.3, 0.03, 0.1, TRIM, [0, 0.62, 0.72])); // cowl vent
  P.push(box(1.32, 0.6, 0.04, SCREEN, [0, 0.8, 0.42], [-SCREEN_RAKE, 0, 0])); // steeply raked windscreen
  for (const s of S) P.push(paint(0.09, 0.6, 0.08, [s * 0.57, 0.8, 0.42], [-SCREEN_RAKE, 0, 0])); // A-pillars
  P.push(paint(1.3, 0.06, 0.74, [0, 0.97, -0.19])); // low flat roof, edges overlap screen + buttresses
  P.push(box(1.16, 0.32, 0.8, CABIN, [0, 0.7, -0.08])); // narrow dark interior block, inboard of the body
  for (const s of S) {
    P.push(box(0.03, 0.24, 0.44, GLASS, [s * 0.585, 0.78, -0.03], [0, 0, s * 0.05])); // door side glass
    P.push(box(0.05, 0.24, 0.06, TRIM, [s * 0.59, 0.78, -0.25], [0, 0, s * 0.05])); // B-pillar
    // Body-colour "flying buttress": a BIG solid sail from the roof down onto
    // the deck, OUTBOARD of the glass, so the rear quarter reads RED not black
    // and the cabin sits flush against the slatted deck (no dark notch).
    P.push(paint(0.16, 0.5, 0.66, [s * 0.6, 0.66, -0.52], [-0.3, 0, 0]));
  }
  P.push(box(0.8, 0.2, 0.04, GLASS, [0, 0.82, -0.58], [0.4, 0, 0])); // small rear window recessed between the buttresses

  // -- THE SIGNATURE SIDE STRAKES: LONG horizontal cheese-grater intakes ------
  // Five bold black blades over body-colour gaps run the WHOLE flank at the
  // same band-heights: a door segment on the tub, stepping OUT onto the wider
  // hip as a taller rear-intake segment ahead of the rear wheel. This is THE
  // Testarossa cue — it must read unmistakably in SIDE and ISO.
  for (const s of S) {
    P.push(box(0.05, 0.3, 0.62, MESH, [s * 0.985, 0.49, -0.54])); // deep rear-intake cavity backing
    for (const y of STRAKE_Y) {
      // door segment: bold blade standing well proud, from just behind the
      // front wheel back to the hip...
      P.push(box(0.07, 0.036, 1.12, STRAKE, [s * 0.94, y, 0.33]));
      // ...stepping OUT onto the wider hip as the taller rear-intake blades.
      P.push(box(0.08, 0.04, 0.6, STRAKE, [s * 1.0, y, -0.53]));
    }
  }

  // -- Sills ------------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.05, 0.14, 2.4, CLAD, [s * 0.915, 0.3, -0.05])); // rocker sill, reaches both arches
  }

  // -- Front fascia: flush pop-up lamps seated into the low nose --------------
  for (const s of S) {
    P.push(box(0.36, 0.06, 0.24, TRIM, [s * 0.5, 0.51, 1.72], [HOOD_SLOPE, 0, 0])); // pop-up housing, flush
    P.push(box(0.32, 0.03, 0.19, LAMP, [s * 0.5, 0.545, 1.72], [HOOD_SLOPE, 0, 0])); // toned lens (pop-up, not sticker)
  }
  P.push(cyl(0.045, 0.045, 0.02, 16, CHROME, [0, 0.46, 2.235], [Math.PI / 2, 0, 0])); // nose badge disc
  P.push(box(0.42, 0.09, 0.02, PLATE, [0, 0.3, 2.24])); // small low front plate

  // -- Rear fascia: FULL-WIDTH horizontal slatted band over a body bumper -----
  // The red lamp band sits behind the black slats (lamps-behind-slats), with a
  // clean body-colour bumper below — no odd bright stripe.
  P.push(box(1.8, 0.28, 0.06, TRIM, [0, 0.62, -2.06])); // black grille panel spanning the width
  P.push(box(1.64, 0.24, 0.04, TAIL_RED, [0, 0.62, -2.09])); // tail lamp band hidden behind the slats
  for (let i = 0; i < 6; i++) {
    const y = 0.52 + i * 0.042; // six thin horizontal slats — red shows in the gaps
    P.push(box(1.72, 0.02, 0.03, MESH, [0, y, -2.12]));
  }
  P.push(paint(1.82, 0.2, 0.14, [0, 0.38, -2.08])); // body-colour rear bumper below the grille
  P.push(box(1.5, 0.13, 0.14, CLAD, [0, 0.24, -2.04])); // rear diffuser valance
  for (const dx of [-0.16, 0.16]) {
    P.push(cyl(0.055, 0.055, 0.09, 12, EXHAUST, [dx, 0.26, -2.13], [Math.PI / 2, 0, 0])); // twin exhaust tips
  }
  P.push(box(0.42, 0.09, 0.02, PLATE, [-0.45, 0.4, -2.13])); // small rear plate, offset to one side

  // -- Furniture: the famous single high-mounted door mirror (left) ----------
  P.push(box(0.03, 0.2, 0.03, TRIM, [-0.9, 0.72, 0.62])); // tall mirror stalk
  P.push(paint(0.05, 0.1, 0.15, [-0.94, 0.85, 0.6])); // mirror head, high on the stalk
  P.push(box(0.012, 0.08, 0.13, GLASS, [-0.968, 0.85, 0.6])); // mirror glass, facing back
  for (const s of S) {
    P.push(box(0.025, 0.025, 0.13, CHROME, [s * 0.915, 0.55, -0.1])); // door handle
    P.push(box(0.3, 0.012, 0.03, TRIM, [s * 0.26, 0.62, 0.68], [-SCREEN_RAKE, 0, 0])); // wiper on the glass
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X_R, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X_R, WHEEL_R, AXLE_R] },
];

export const ferrariTestarossa = defineModel({
  id: "ferrari-testarossa",
  name: "Ferrari Testarossa",
  batchId: "cars",
  tags: ["car", "ferrari", "testarossa", "supercar", "sports", "classic"],
  summary: "A Ferrari Testarossa flat wide mid-engine wedge at true scale, in Rosso Corsa.",
  description:
    "A 1984 Ferrari Testarossa at real 1 unit = 1 metre scale (about 4.49 " +
    "long, a famously wide 1.98, very low and flat, 2.55 wheelbase). The " +
    "silhouette is one continuous low wedge: a low pointed nose with flush " +
    "pop-up headlamps, a steeply raked windscreen over a low flat cabin cap, " +
    "a dark slatted engine cover behind the cabin (never an open deck), and a " +
    "low kicked-up tail. The signature cues are all present: the LONG " +
    "horizontal cheese-grater SIDE STRAKES running the whole flank and " +
    "flaring into the wide rear intakes ahead of the rear wheels, dramatic " +
    "wide rear haunches that make the tail far wider than the nose, a " +
    "body-colour flying-buttress B-pillar flush to the deck, the full-width " +
    "horizontal slatted TAIL band with the lamps hidden behind it over a " +
    "body-colour bumper, the famous single high-mounted door mirror, and " +
    "bright five-spoke alloys on low-profile tyres. Body panels carry the " +
    "'paint' recolour slot (Rosso Corsa); `drive` spins the wheels and " +
    "`hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.4,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
